import { Fragment, useEffect } from 'react';
import { Alert, Col, Row } from 'antd';
import { DataNode } from 'antd/lib/tree';

import { MODULES } from '~src/types/enums';
import {
  ParentInviterVM,
  TenderCoreRegVM,
  TenderRegistrationVM,
} from '~src/types/db';
import { useRegistrationCoreTendersDetails } from '~src/hooks/api-hooks/useRegistrationCoreTendersDetails';
import { formatDate } from '~src/utils/formatDate';
import { useIntl } from 'react-intl';

import CollapseList from './components/DetailsCollapseList';
import RightDetailsBlock from './components/RightDetailsBlock/RightDetailsBlock';
import LeftDetailsBlock from './components/LeftDetailsBlock/LeftDetailsBlock';

import TenderDocumentExpandable from '../RightSideView/components/TenderDocumentsPrint/TenderDocumentsPrint';
import TenderSharesDocumentExpandable from '../RightSideView/components/TenderDocumentsPrint/TenderSharesDocumentExpandable';
import TenderSharedContractPrint from '~components/molecules/RightSideView/components/TenderContractsPrint/TenderSharedContractPrint';
import TenderContractPrint from '~components/molecules/RightSideView/components/TenderContractsPrint/TenderContractPrint';
import { useIsPath } from '~src/hooks/useIsPath';

export type StateDataNode<MetaT = unknown> = DataNode & {
  path?: string;
  children?: StateDataNode<MetaT>[];
  parentId?: string | null;
  favourite?: boolean;
  moduleId?: MODULES;
  isTender?: boolean;
  keyPath?: string | null;
  type?: string | null;
  isTenderRootFolder?: string | null;
  tenderRegistrationId?: string | null;
  firstElementId?: string;
  isUnassigned?: boolean;
  meta?: MetaT;
};

export type TenderDetailsProps = {
  projectId?: string;
  tenderId?: string;
  tenderData?: TenderRegistrationVM | TenderCoreRegVM;
  isTenderPage?: boolean;
  isShares?: {
    shareId: string;
    shareKey: string;
  };
  isValidShare?: boolean;
  isPublicTender?: boolean;
  // Primary-/Secondary Share Keys: when set, this tender notice is being
  // viewed through a Secondary-Share-Key - render the JIRA-required
  // disclaimer + the inviting Primary-Share-Key's contact info, and hide
  // the embedded Contract collapse panel below (no contract-offer access
  // for a Secondary-Share-Key, same 401 rule as the standalone Contract tab).
  secondaryKeyInfo?: { parentInviter?: ParentInviterVM | null } | null;
};

const TenderDetails: React.FC<TenderDetailsProps> = ({
  tenderData,
  projectId,
  tenderId,
  isTenderPage = false,
  isValidShare = true,
  isShares,
  isPublicTender = false,
  secondaryKeyInfo,
}) => {
  const intl = useIntl();
  const coreTendersDetails = useRegistrationCoreTendersDetails({
    tenderId,
    isShares,
  });
  const tenderDetails = coreTendersDetails ?? tenderData;
  const isInvitationsRoute = useIsPath('/invitations');

  const documentsCollapseItem = {
    title: intl.formatMessage({ id: 'tab-documents' }),
    content: isShares ? (
      <div style={{ maxHeight: 550, overflowY: 'auto' }}>
        <p
          style={{
            marginTop: 12,
            fontSize: 13,
            color: '#888',
            fontStyle: 'italic',
            paddingLeft: 8,
          }}>
          {intl.formatMessage({ id: 'documents-panel-help-text' })}
        </p>
        <TenderSharesDocumentExpandable
          tenderId={tenderId ?? ''}
          shareId={isShares.shareId}
          shareKey={isShares.shareKey}
          isValidShare={isValidShare}
          registrationAttachments={
            (tenderDetails as any)?.attachments ??
            (tenderData as any)?.attachments ??
            []
          }
          isStartFetch
          isExpandable
        />
      </div>
    ) : isPublicTender && tenderId ? (
      <div style={{ maxHeight: 550, overflowY: 'auto' }}>
        <p
          style={{
            marginTop: 12,
            fontSize: 13,
            color: '#888',
            fontStyle: 'italic',
            paddingLeft: 8,
          }}>
          {intl.formatMessage({ id: 'documents-panel-help-text' })}
        </p>
        <TenderSharesDocumentExpandable
          tenderId={tenderId}
          isPublicTender
          isValidShare={isValidShare}
          registrationAttachments={
            (tenderDetails as any)?.attachments ??
            (tenderData as any)?.attachments ??
            []
          }
          isStartFetch
          isExpandable
        />
      </div>
    ) : projectId && tenderId ? (
      <div style={{ maxHeight: 550, overflowY: 'auto' }}>
        <p
          style={{
            marginTop: 12,
            fontSize: 13,
            color: '#888',
            fontStyle: 'italic',
            paddingLeft: 8,
          }}>
          {intl.formatMessage({ id: 'documents-panel-help-text' })}
        </p>
        <TenderDocumentExpandable
          projectId={projectId ?? ''}
          tenderId={tenderId ?? ''}
          isStartFetch
          isExpandable
        />
      </div>
    ) : null,
  };

  const contractCollapseItem = {
    title: intl.formatMessage({ id: 'contract' }),
    content: isShares ? (
      <div style={{ maxHeight: 550, overflowY: 'auto' }}>
        <TenderSharedContractPrint
          tenderId={tenderId ?? ''}
          shareId={isShares.shareId}
          shareKey={isShares.shareKey}
          isValidShare={isValidShare}
          isStartFetch
          isExpandable
        />
      </div>
    ) : isPublicTender && tenderId ? (
      <div style={{ maxHeight: 550, overflowY: 'auto' }}>
        <TenderSharedContractPrint
          tenderId={tenderId}
          isPublicTender
          isValidShare={isValidShare}
          isStartFetch
          isExpandable
        />
      </div>
    ) : projectId && tenderId ? (
      <div style={{ maxHeight: 550, overflowY: 'auto' }}>
        <TenderContractPrint
          projectId={projectId ?? ''}
          tenderId={tenderId ?? ''}
          isStartFetch
          isExpandable
        />
      </div>
    ) : null,
  };

  // Primary-/Secondary Share Keys: a Secondary-Share-Key has no access to
  // contract/offer data (401 server-side) - omit this embedded panel for it,
  // same rule already applied to the standalone Contract tab in
  // MyTendersTabs.tsx.
  const collapseItems = secondaryKeyInfo
    ? [documentsCollapseItem]
    : [documentsCollapseItem, contractCollapseItem];

  // console.log(`isSharedTender`, !!isShares);
  // console.log(`table items: tenderDetails`, tenderDetails);

  return (
    <Fragment>
      <div style={{ margin: '20px', overflowY: 'auto' }}>
        {/* Primary-/Secondary Share Keys: disclaimer shown on the tender
            notice for a Secondary-Share-Key, same text in both the
            invitation flow (SharedTenderAttachmentsTable.tsx) and the
            My-Tenders flow (MyTendersTabs.tsx) - both render this same
            component. */}
        {secondaryKeyInfo && (
          <Alert
            style={{ marginBottom: 16 }}
            type="warning"
            showIcon
            message={intl.formatMessage({
              id: 'secondary-share-key-tender-notice-disclaimer',
            })}
          />
        )}
        <Row>
          <Col span={18}>
            <LeftDetailsBlock
              isTenderPage={isTenderPage}
              isPublicTender={isPublicTender}
              item={{
                id: undefined,
                title: tenderDetails?.title ?? tenderData?.title,
                descriptionTitle: intl.formatMessage({
                  id: 'description-title',
                }),
                description: tenderDetails?.description,
                reference: tenderDetails?.reference,
                share_title: tenderDetails?.share_title,
                tenderId,
                projectId,
                tableEntries: {
                  active_from: tenderDetails?.active_from,
                  deadline_at: tenderDetails?.deadline_at,
                  publication_date: tenderDetails?.publication_date,
                  publication_at: tenderDetails?.publication_at,
                  active_until: tenderDetails?.active_until,
                  qa_start: tenderDetails?.qa_start,
                  qa_stop: tenderDetails?.qa_stop,
                  qa_on: tenderDetails?.qa_on,
                  results_deadline: tenderDetails?.results_deadline,
                  results_details: tenderDetails?.results_details,
                  execution_period: tenderDetails?.execution_period,
                  execution_period_data: tenderDetails?.execution_period_data,
                  publicShareState: tenderData?.state,
                },
                global_serial: tenderDetails?.global_serial,
                public: tenderDetails?.public ?? tenderData?.public,
                label_values: tenderDetails?.label_values ?? [],
              }}
            />
            {/* JIRA: MVP TV1-420*/}
            <Col span={24} style={{ marginTop: '70px' }}>
              {/*{!isInvitationsRoute && <CollapseList items={collapseItems} />}*/}
              <CollapseList items={collapseItems} />
            </Col>
          </Col>
          <Col span={1} />
          <Col span={5}>
            <RightDetailsBlock
              isTenderPage={isTenderPage}
              primaryVendorContact={secondaryKeyInfo?.parentInviter}
              item={{
                ...tenderDetails,
                id: tenderId,
                projectId: projectId,
                title: tenderDetails?.title ?? tenderData?.title,
              }}
            />
          </Col>
        </Row>
      </div>
    </Fragment>
  );
};

export default TenderDetails;
